import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";

import { cn } from "@/utils/cn";

type Props = {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  hint?: string;
  badge?: string;
  type?: "text" | "email" | "tel" | "password";
  autoComplete?: string;
  error?: string | null;
};

export function Field({
  label,
  value,
  onChange,
  readOnly,
  placeholder,
  hint,
  badge,
  type = "text",
  autoComplete,
  error,
}: Props) {
  const [reveal, setReveal] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && reveal ? "text" : type;

  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between">
        <span className="field-label">{label}</span>
        {badge ? (
          <span className="chip bg-slate-100 text-slate-600">{badge}</span>
        ) : null}
      </div>
      <div className="relative">
        <input
          type={effectiveType}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          readOnly={readOnly}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={cn(
            "field-input",
            readOnly && "cursor-not-allowed bg-slate-50 text-slate-500",
            isPassword && "pr-10",
            error && "border-red-400 focus-visible:border-red-500 focus-visible:ring-red-500/30",
          )}
        />
        {isPassword ? (
          <button
            type="button"
            onClick={() => setReveal((r) => !r)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700"
            aria-label={reveal ? "Hide password" : "Show password"}
            tabIndex={-1}
          >
            {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        ) : null}
      </div>
      {error ? (
        <p className="mt-1 text-xs font-medium text-red-700">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </label>
  );
}
